import classNames from 'classnames';
import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

// components
import {
    BreadCrumbContainerStyled,
    BodyContainerStyled,
    GridContainerStyled,
    CartMemberPanelContainerStyled,
    CartEventAdsContainerStyled,
} from './index.styled';

import CartEventAds from './CartEventAds';
import CartMemberPanel from './CartMemberPanel';
import CartUpsell from './CartUpsell';
import FooterNavigation from '../../../_molecules/FooterNavigation';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import CartSummaryMobile from '../../../_organisms/CartSummaryMobile';
import CartSummarySidebar from '../../../_organisms/CartSummarySidebar';
import CartUpsellMembershipModal from '@modals/CartUpsellMembershipModal';
import CartCrossSellModal from '@modals/CartCrossSellModal';
import NotificationBanner from '@molecules/NotificationBanner';
import StepperNavMobile from '@molecules/StepperNavMobile';
import { BodyMedium, BodySmall, DividerBlueStyled, DivWithShadowAlternate, H3 } from '@styled';

// functionality
import useCartMembersWithSpeedBumps from './useCartMembersWithSpeedBumps';
import { LoadingContext } from '@context/LoadingProvider';
import { processCartErrors, removeOrphanedDonations, resetFlashNotices } from '@general/helpers/cart.helpers';
import productTypes from '@general/productTypes';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventToRegisterSessionsFor from '@hooks/useEventToRegisterSessionsFor';
import useCartItemsGroupedByMember from '@hooks/useCartItemsGroupedByMember';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import useIcscTracking from '@react/tracking/useIcscTracking';
import { getMemberInfoBreadcrumbLink } from '@general/helpers/member.helpers';
import { getLoginPath } from '../../../../helpers/url.helpers';

const CartViewPage = () => {
    // ------------------------------
    // State
    // ------------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [memberIdsToLoad, setMemberIdsToLoad] = useState();
    const [isMembershipUpsellModalOpen, setIsMembershipUpsellModalOpen] = useState(false);
    const [isCrossSellModalOpen, setIsCrossSellModalOpen] = useState(false);
    const [crossSellEventCode, setCrossSellEventCode] = useState(null);
    const [crossSellModalShown, setCrossSellModalShown] = useState(false);
    const [cartHasErrors, setCartHasErrors] = useState(false);
    const [optionalFullProgramEvents, setOptionalFullProgramEvents] = useState([]);

    // ------------------------------
    // Hooks
    // ------------------------------

    const { eventToRegisterSessionsFor } = useEventToRegisterSessionsFor();
    const { cartMembersWithSpeedBumps } = useCartMembersWithSpeedBumps();
    const history = useHistory();
    const { Track } = useIcscTracking({
        app_area: 'Cart View',
    });

    const { viewer } = useViewer();
    const { cart, refetch: refetchCart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { itemsGroupedByMember } = useCartItemsGroupedByMember({
        cart,
        itemCount: cart ? cart.items.length : null,
        optionalFullProgramEvents,
        total: cart?.total,
    });

    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();

    // ------------------------------
    // Effects
    // ------------------------------

    // Open loading overlay on first load
    useEffect(() => {
        if (isFirstLoad) {
            setIsLoading(true);
        }
    }, [isFirstLoad, setIsLoading]);

    // Close loading overlay if cart is loaded but has no items
    useEffect(() => {
        if (!cart) {
            return;
        }

        if (cart.items.length === 0) {
            setIsLoading(false);
        }
    }, [cart, setIsLoading]);

    // Process cart errors, remove orphaned donations, and reset flash notices
    useEffect(() => {
        if (!isFirstLoad || !cart) {
            return;
        }

        setIsFirstLoad(false);

        (async () => {
            let updatedCart = await processCartErrors({ cart, viewer });
            updatedCart = await removeOrphanedDonations({ cart: updatedCart, viewer });
            resetFlashNotices({ cart: updatedCart, viewer });
        })();
    }, [cart, viewer, isFirstLoad]);

    // Set memberIdsToLoad for the purpose of tracking which components on the page have loaded,
    // which makes it possible to close the loading overlay when and only when all components have loaded
    useEffect(() => {
        if (!itemsGroupedByMember) {
            return;
        }

        const memberIds = itemsGroupedByMember.map(member => member.memberId);
        setMemberIdsToLoad(
            memberIds.map(memberId => ({
                memberId,
                loaded: false,
            }))
        );
    }, [itemsGroupedByMember]);

    useEffect(() => {
        if (!cart || !cart.items || crossSellModalShown) {
            return;
        }

        // Define cross-sell event configurations
        const CROSS_SELL_EVENTS = [
            // Bidirectional pair - if one is in cart, promote the other
            // {
            //     type: 'pair',
            //     events: ['2025WS', '2025RETAIL_LB']
            // },
            // Unidirectional - if trigger event is in cart, promote target event
            {
                type: 'single',
                triggerEvent: '2025WS',
                targetEvent: '2025RETAIL_LB',
            },
        ];

        // Get all event codes in the cart
        const cartEventCodes = cart.items
            .filter(item => item.meta_info?.event_code)
            .map(item => item.meta_info.event_code);

        // Check each configuration
        for (const config of CROSS_SELL_EVENTS) {
            if (config.type === 'pair') {
                // Handle bidirectional pairs (existing logic)
                const eventsInCartForThisPair = cartEventCodes.filter(code =>
                    config.events.includes(code)
                );

                // Only show modal if exactly one event from this pair is in cart
                if (eventsInCartForThisPair.length === 1) {
                    const eventInCart = eventsInCartForThisPair[0];
                    const eventToPromote = config.events.find(code => code !== eventInCart);
                    setCrossSellEventCode(eventToPromote);
                    setIsCrossSellModalOpen(true);
                    setCrossSellModalShown(true);
                    return; // Exit - we found a match
                }
            } else if (config.type === 'single') {
                // Handle unidirectional single event triggers
                const hasTriggerEvent = cartEventCodes.includes(config.triggerEvent);
                const hasTargetEvent = cartEventCodes.includes(config.targetEvent);

                // Show modal if trigger event is in cart but target event is not
                if (hasTriggerEvent && !hasTargetEvent) {
                    setCrossSellEventCode(config.targetEvent);
                    setIsCrossSellModalOpen(true);
                    setCrossSellModalShown(true);
                    return; // Exit - we found a match
                }
            }
        }
    }, [cart, crossSellModalShown]);

    // ------------------------------
    // Functions
    // ------------------------------

    const onNextClick = useCallback(
        async () => {
            await refetchCart();
            await resetFlashNotices({ cart, viewer });
            history.push('/cart/payment');
        },
        [cart, history, refetchCart, viewer],
        history
    );

    const trackLoadedMembers = ({ memberId }) => {
        const memberIndex = memberIdsToLoad.findIndex(member => member.memberId === memberId);
        memberIdsToLoad[memberIndex].loaded = true;
        setMemberIdsToLoad([...memberIdsToLoad]);

        if (memberIdsToLoad.every(member => member.loaded)) {
            setIsLoading(false);
        }
    };

    const handleCrossSellModalClose = () => {
        setIsCrossSellModalOpen(false);
    };

    // ------------------------------
    // Render
    // ------------------------------
    if (viewer === null) {
        window.location.href = getLoginPath();
    }

    if (!itemsGroupedByMember) {
        return null;
    }

    const isCartEmpty = cart.items.length === 0;
    const cartHasMemberships = cart.items.some(item => item.product_type === productTypes.MEMBERSHIP);

    return (
        <Track>
            <div>
                {!isCartEmpty && isScreenMinMd && (
                    <BreadCrumbContainerStyled>
                        <div className="container">
                            <div className="py-1rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb route={getMemberInfoBreadcrumbLink(cart, viewer?.recordId)}>
                                        Account Info
                                    </SectionBreadcrumb>
                                    <SectionBreadcrumb isActive>Cart Details</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/payment">Payment Info</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>
                        </div>
                    </BreadCrumbContainerStyled>
                )}

                {!isCartEmpty && isScreenSmDown && (
                    <StepperNavMobile
                        activeIndex={1}
                        steps={[
                            { label: 'Account Info', href: getMemberInfoBreadcrumbLink(cart, viewer?.recordId) },
                            { label: 'Cart Details', href: '/cart/view' },
                            { label: 'Payment Info', href: '/cart/payment' },
                        ]}
                    />
                )}

                <BodyContainerStyled className="container">
                    <div>
                        <H3>Cart</H3>

                        {!isCartEmpty && isScreenMinMd && (
                            <BodyMedium className="mt-0.25rem">Select your options.</BodyMedium>
                        )}

                        {!isCartEmpty && isScreenSmDown && (
                            <BodySmall className="mt-0.5rem">Select your options.</BodySmall>
                        )}

                        <GridContainerStyled>
                            <div className="col-span-5">
                                <DividerBlueStyled
                                    className={classNames('mt-0', {
                                        'mb-0.625rem': isScreenSmDown,
                                        'mb-1rem': isScreenMinMd,
                                    })}
                                />

                                {cartMembersWithSpeedBumps && cartMembersWithSpeedBumps.length > 0 && (
                                    <NotificationBanner>
                                        Membership must be active at the time of the event in order to receive the
                                        member discount. Membership has been added for the following:
                                        <ul className="mb-0 pl-1rem">
                                            {cartMembersWithSpeedBumps.map(member => {
                                                if (window.heap) {
                                                    window.heap.track('Speed Bump', { memberId: member.account_id });
                                                }

                                                return (
                                                    <li key={member.id}>
                                                        {member.first_name} {member.last_name}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </NotificationBanner>
                                )}

                                {isCartEmpty &&
                                    !eventToRegisterSessionsFor &&
                                    optionalFullProgramEvents?.length === 0 && (
                                        <NotificationBanner>Your cart is empty</NotificationBanner>
                                    )}

                                {(!isCartEmpty ||
                                    eventToRegisterSessionsFor ||
                                    optionalFullProgramEvents?.length > 0) && (
                                    <div>
                                        {itemsGroupedByMember.map((memberGroup, index) => {
                                            return (
                                                <CartMemberPanelContainerStyled key={memberGroup.memberId}>
                                                    <CartMemberPanel
                                                        isOpen={index === itemsGroupedByMember.length - 1}
                                                        {...memberGroup}
                                                        onComponentLoaded={() => {
                                                            trackLoadedMembers({ memberId: memberGroup.memberId });
                                                        }}
                                                        setOptionalFullProgramEvents={setOptionalFullProgramEvents}
                                                    />
                                                </CartMemberPanelContainerStyled>
                                            );
                                        })}
                                    </div>
                                )}

                                {cartHasMemberships && (
                                    <DivWithShadowAlternate
                                        className={classNames({
                                            'mt-1.5rem': isScreenSmDown,
                                            'mt-3rem': isScreenMinMd,
                                        })}
                                    >
                                        <div
                                            className={classNames({
                                                'px-1.5rem py-1.125rem': isScreenSmDown,
                                                'p-1.5rem': isScreenMinMd,
                                            })}
                                        >
                                            <CartUpsell
                                                text="Add More Memberships To My Order"
                                                onClick={() => setIsMembershipUpsellModalOpen(true)}
                                            />

                                            <CartUpsellMembershipModal
                                                isOpen={isMembershipUpsellModalOpen}
                                                onClose={() => setIsMembershipUpsellModalOpen(false)}
                                            />
                                        </div>
                                    </DivWithShadowAlternate>
                                )}
                            </div>

                            {isScreenMinMd && (
                                <div className="col-span-3">
                                    <CartSummarySidebar setCartHasErrors={setCartHasErrors} />
                                </div>
                            )}
                        </GridContainerStyled>
                    </div>
                </BodyContainerStyled>

                <CartEventAdsContainerStyled>
                    <CartEventAds />
                </CartEventAdsContainerStyled>

                {!isCartEmpty && isScreenMinMd && (
                    <FooterNavigation
                        // nextButtonDisabled={cartHasErrors}
                        previousLabel="Account Info"
                        nextLabel="Payment Info"
                        showPreviousCaret
                        onPreviousClick={() => history.push(getMemberInfoBreadcrumbLink(cart, viewer?.recordId))}
                        onNextClick={onNextClick}
                    />
                )}

                {!isCartEmpty && isScreenSmDown && (
                    <CartSummaryMobile
                        setCartHasErrors={setCartHasErrors}
                        navLabel="Payment Info"
                        onNavClick={onNextClick}
                    />
                )}

                {crossSellEventCode && (
                    <CartCrossSellModal
                        eventCode={crossSellEventCode}
                        isOpen={isCrossSellModalOpen}
                        onClose={handleCrossSellModalClose}
                    />
                )}
            </div>
        </Track>
    );
};

export default CartViewPage;